import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_AiMusicGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/ai-music-generator',
			name: 'AI Music Generator: Turn Text into Original Tracks',
			description:
				"Lift's AI music maker builds a full track from a short description. Pick a genre, add lyrics or keep it instrumental, and start creating now.",
			url: 'https://liftapp.ai/ai-music-generator',
			hasPart: [
				{ '@id': 'https://liftapp.ai/ai-music-generator#product' },
				{ '@id': 'https://liftapp.ai/ai-music-generator#howto' },
				{ '@id': 'https://liftapp.ai/ai-music-generator#benefits' },
				{ '@id': 'https://liftapp.ai/ai-music-generator#faq' },
				{ '@id': 'https://liftapp.ai/ai-music-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/ai-music-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/ai-music-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/ai-music-generator#product',
			name: 'AI Music Generator',
			description:
				'Describe a genre and vibe, add lyrics or go instrumental, and get an original track to play and download. Start creating music with AI today.',
			url: 'https://liftapp.ai/ai-music-generator',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/ai-music-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Describe the style of your track',
					text: 'Type the genre, vibe, and instruments you want, or tap a genre tag to set the sound.',
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: 'Add lyrics or keep it instrumental',
					text: 'Write your own lyrics in any language, let AI draft them, or switch to an instrumental track.',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Choose the voice and track length',
					text: 'Pick a female or male voice for vocal tracks, then set the length or leave it on Auto.',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Generate, play, and download your track',
					text: 'Generate the track, play it right away, and download it or regenerate with the same settings.',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-music-generator#benefits',
			name: 'Who can benefit from the AI Music Generator?',
			description:
				'Lift\u2019s AI Music Generator helps creators, songwriters, and small businesses turn a short text description into an original track with or without vocals.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Content creators — Generate original background tracks that fit the pace of your vlogs, reels, and shorts.',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Songwriters and hobby musicians — Turn lyrics or rough ideas into a full track with vocals to hear how they sound.',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Gift givers — Write lyrics about a birthday, wedding, or anniversary and turn them into a personal song.',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Small business owners — Create a short jingle or instrumental track for ads, promo videos, and social posts.',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Podcasters — Generate an instrumental intro and outro that give each episode a recognizable sound.',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/images/ai-music-generator/benefits.webp',
				name: 'AI Music Generator by Lift',
				description:
					"Tablet showing generated tracks from Lift's AI music creator, with one track playing and ready to download",
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/ai-music-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is the Lift AI Music Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'The Lift AI Music Generator turns a text description of genre, tempo, and instruments into an original track. You can create instrumental music or full songs with vocals from your own lyrics.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does the Lift AI Music Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Describe the style, add lyrics or turn on Instrumental, and choose the voice and track length. Tap Generate to get your track, then play it, download it, or regenerate it with the same settings.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I create a track without vocals or lyrics?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Turn on Instrumental to generate music without vocals, or leave the lyrics field empty and Lift creates an instrumental track automatically. To add singing, switch Instrumental off and write or generate your lyrics.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I write lyrics for my track in any language?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. The lyrics field accepts text in any language, so you can write your words exactly the way you want them sung.',
					},
				},
				{
					'@type': 'Question',
					name: "What if I don't have lyrics ready?",
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Tap Help me write lyrics, and AI drafts the text for you. Keep it as is or edit it before you generate the track.',
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-music-generator#why-lift',
			name: 'Why choose Lift for AI music',
			description:
				'Lift\u2019s AI Music Generator combines genre control, optional lyrics, and vocal settings in one affordable tool available on iOS, Android, and Web.',
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl: 'https://liftapp.ai/images/ai-music-generator/whyLift.webp',
				name: 'Why use Lift AI Music Generator',
				description:
					'Phone beside a yoga mat generating an energetic AI music track, next to vocal gender and duration settings',
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/ai-music-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/ai-music-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'AI Music Generator',
					item: 'https://liftapp.ai/ai-music-generator',
				},
			],
		},
	],
};
